/**
 * NodeActionContext — Provides callbacks for node hover actions, staged node
 * actions, and navigation focus state.
 *
 * Only BaseNode consumes the context; concrete nodes just pass nodeId through.
 */

import { createContext, useContext, type ReactNode } from 'react';

interface NodeActionContextValue {
  onNodeAction: (nodeId: string, nodeLabel: string, nodeType: string) => void;
  activeNodeId: string | null;
  // Staged node actions
  onConfirmNode: (nodeId: string) => void;
  onEditNode: (nodeId: string) => void;
  onDismissNode: (nodeId: string) => void;
  // Navigation focus
  focusedNodeId: string | null;
  // Agent highlight state
  highlightedNodeIds: Set<string>;
}

const NodeActionContext = createContext<NodeActionContextValue | null>(null);

const emptySet = new Set<string>();

export function NodeActionProvider({
  onNodeAction,
  activeNodeId,
  onConfirmNode,
  onEditNode,
  onDismissNode,
  focusedNodeId,
  highlightedNodeIds,
  children,
}: NodeActionContextValue & { children: ReactNode }) {
  return (
    <NodeActionContext.Provider
      value={{ onNodeAction, activeNodeId, onConfirmNode, onEditNode, onDismissNode, focusedNodeId, highlightedNodeIds }}
    >
      {children}
    </NodeActionContext.Provider>
  );
}

const noop = () => {};

export function useNodeAction(): NodeActionContextValue {
  const ctx = useContext(NodeActionContext);
  if (!ctx) {
    return {
      onNodeAction: noop,
      activeNodeId: null,
      onConfirmNode: noop,
      onEditNode: noop,
      onDismissNode: noop,
      focusedNodeId: null,
      highlightedNodeIds: emptySet,
    };
  }
  return ctx;
}
